import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { OrderSide, OrderType, AmountType, MarginMode, PriceType } from '../../types/preset';
import { infoService } from '../../services/infoService';

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  background-color: #262626;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 0.8rem;
  margin-bottom: 1rem;
`;

const PreviewTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #ddd;
  border-bottom: 1px solid #333;
  padding-bottom: 0.5rem;
  margin-bottom: 0.2rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
`;

const DetailLabel = styled.div`
  color: #999;
`;

const DetailValue = styled.div<{ highlight?: boolean; warning?: boolean; danger?: boolean }>`
  color: ${({ highlight, warning, danger }) => (
    danger ? '#ef4444' : warning ? '#f87171' : highlight ? '#10b981' : '#ddd'
  )};
  font-weight: ${({ highlight, warning, danger }) => (highlight || warning || danger ? 'bold' : 'normal')};
`;

const SectionDivider = styled.div`
  height: 1px;
  background-color: #333;
  margin: 0.5rem 0;
`;

const WarningBox = styled.div`
  background-color: rgba(239, 68, 68, 0.15);
  border: 1px solid #ef4444;
  border-radius: 4px;
  padding: 0.5rem 0.8rem;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #f87171;
`;

interface OrderPreviewProps {
  symbol: string;
  side: OrderSide;
  orderType: OrderType;
  amount: string;
  amountType: AmountType;
  leverage: string;
  limitPrice?: string;
  limitPriceType?: PriceType;
  priceOffset?: string;
  stopPrice?: string;
  takeProfitPrice?: string;
  stopLossPrice?: string;
  marginMode: MarginMode;
  postOnly: boolean;
  reduceOnly: boolean;
}

const OrderPreview: React.FC<OrderPreviewProps> = ({
  symbol,
  side,
  orderType,
  amount,
  amountType,
  leverage,
  limitPrice,
  limitPriceType,
  priceOffset,
  stopPrice,
  takeProfitPrice,
  stopLossPrice,
  marginMode,
  postOnly,
  reduceOnly,
}) => {
  // State for calculated values
  const [markPrice, setMarkPrice] = useState<number | null>(null);
  const [bestBid, setBestBid] = useState<number | null>(null);
  const [bestAsk, setBestAsk] = useState<number | null>(null);
  const [estimatedFee, setEstimatedFee] = useState<number | null>(null);
  const [liquidationPrice, setLiquidationPrice] = useState<number | null>(null);
  const [liquidationDistance, setLiquidationDistance] = useState<number | null>(null);
  const [liquidationPercentage, setLiquidationPercentage] = useState<number | null>(null);
  const [marginImpact, setMarginImpact] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate estimated entry price
  const getEstimatedEntryPrice = (): number | null => {
    if (orderType === 'limit' && limitPriceType === 'best_bid') {
       const offset = priceOffset ? parseFloat(priceOffset) : 0;
       return bestBid ? bestBid + offset : null;
    } else if (orderType === 'limit' && limitPriceType === 'best_ask') {
       const offset = priceOffset ? parseFloat(priceOffset) : 0;
       return bestAsk ? bestAsk + offset : null;
    } else if (orderType === 'market') {
      // Use best ask for buy, best bid for sell (assuming slippage)
      return side === 'buy' ? bestAsk : bestBid;
    } else if (orderType === 'limit' && limitPriceType === 'absolute' && limitPrice) {
      return parseFloat(limitPrice);
    } else if ((orderType === 'stop_market' || orderType === 'stop_limit') && stopPrice) {
      return parseFloat(stopPrice);
    }
    return markPrice;
  };

  // Calculate position value
  const getPositionValue = (): number | null => {
    const entryPrice = getEstimatedEntryPrice();
    if (!entryPrice || !amount) return null;

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) return null;

    if (amountType === 'base') {
      return amountNum * entryPrice;
    } else if (amountType === 'quote') {
      return amountNum;
    } else if (amountType === 'percent') {
      // Placeholder - would need account value
      return null;
    }
    return null;
  };

  // Calculate required margin
  const getRequiredMargin = (): number | null => {
    const posValue = getPositionValue();
    if (!posValue || !leverage) return null;
    
    const leverageNum = parseFloat(leverage);
    if (isNaN(leverageNum) || leverageNum <= 0) return null;
    
    return posValue / leverageNum;
  };

  // Calculate estimated fee
  const calculateFee = (): number | null => {
    const posValue = getPositionValue();
    if (!posValue) return null;
    
    // HyperLiquid fee rates (example - would need to get from API)
    const takerFeeRate = 0.0010; // 0.10% for takers
    const makerFeeRate = 0.0005; // 0.05% for makers
    
    // Assume taker fee for market orders and maker fee for limit orders with post-only
    const feeRate = (orderType === 'market' || !postOnly) ? takerFeeRate : makerFeeRate;
    
    return posValue * feeRate;
  };

  // Calculate estimated liquidation price
  const calculateLiquidationPrice = (): number | null => {
    const entryPrice = getEstimatedEntryPrice();
    if (!entryPrice || !leverage) return null;
    
    const leverageNum = parseFloat(leverage);
    if (isNaN(leverageNum) || leverageNum <= 0) return null;
    
    // Improved liquidation price calculation (still simplified)
    // Consider maintenance margin and fees
    const maintenanceMargin = 0.02; // 2% maintenance margin (sample value)
    const initialMargin = 1 / leverageNum;
    const safetyMargin = initialMargin - maintenanceMargin;
    
    if (side === 'buy') {
      return entryPrice * (1 - safetyMargin);
    } else {
      return entryPrice * (1 + safetyMargin);
    }
  };

  // Calculate liquidation distance as a percentage
  const calculateLiquidationPercentage = (): number | null => {
    const entryPrice = getEstimatedEntryPrice();
    const liqPrice = liquidationPrice;
    
    if (!entryPrice || !liqPrice) return null;
    
    const percentage = side === 'buy'
      ? ((entryPrice - liqPrice) / entryPrice) * 100
      : ((liqPrice - entryPrice) / entryPrice) * 100;
    
    return percentage;
  };

  // Calculate margin impact (how much of account balance this uses)
  const calculateMarginImpact = (): number | null => {
    const requiredMargin = getRequiredMargin();
    
    // Mock account balance - in real app, use account data service
    const accountBalance = 10000; // Example $10,000 USD
    
    if (!requiredMargin || !accountBalance) return null;
    
    return (requiredMargin / accountBalance) * 100;
  };

  // Fetch market data on component mount or when props change
  useEffect(() => {
    const fetchMarketData = async () => {
      if (!symbol) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch mark price
        const markPriceData = await infoService.getMarkPrice(symbol);
        setMarkPrice(markPriceData);
        
        // Fetch best bid/ask
        const orderBookData = await infoService.getOrderbookData(symbol);
        setBestBid(orderBookData.best_bid);
        setBestAsk(orderBookData.best_ask);
        
        // Calculate derived values once we have the data
      } catch (err) {
        console.error('Error fetching market data for preview:', err);
        setError('Failed to load market data for preview');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMarketData();
  }, [symbol]);

  // Calculate derived values when inputs change
  useEffect(() => {
    // Calculate fee
    const fee = calculateFee();
    setEstimatedFee(fee);
    
    // Calculate liquidation price
    const liqPrice = calculateLiquidationPrice();
    setLiquidationPrice(liqPrice);
    
    // Calculate liquidation percentage
    const liqPercentage = calculateLiquidationPercentage();
    setLiquidationPercentage(liqPercentage);
    
    // Calculate margin impact
    const impact = calculateMarginImpact();
    setMarginImpact(impact);
    
    // Calculate liquidation distance in price terms
    const entryPrice = getEstimatedEntryPrice();
    if (entryPrice && liqPrice) {
      setLiquidationDistance(Math.abs(entryPrice - liqPrice));
    } else {
      setLiquidationDistance(null);
    }
  }, [symbol, side, orderType, amount, amountType, leverage, limitPrice, limitPriceType, priceOffset, markPrice, bestBid, bestAsk]);

  // Don't show preview if core values are missing
  if (!symbol || !amount || isLoading) {
    return null;
  }

  const entryPrice = getEstimatedEntryPrice();
  const positionValue = getPositionValue();
  const requiredMargin = getRequiredMargin();

  // Determine if the liquidation distance is dangerously close
  const isDangerousLeverage = liquidationPercentage !== null && liquidationPercentage < 5;

  // Format price display
  const formatPriceDisplay = () => {
      if (orderType === 'limit') {
          if (limitPriceType === 'best_bid') {
              const offsetVal = priceOffset && parseFloat(priceOffset) !== 0 ? ` ${parseFloat(priceOffset) >= 0 ? '+' : ''}${priceOffset}` : '';
              return `Best Bid${offsetVal} (${bestBid?.toFixed(2) ?? 'N/A'})`;
          } else if (limitPriceType === 'best_ask') {
              const offsetVal = priceOffset && parseFloat(priceOffset) !== 0 ? ` ${parseFloat(priceOffset) >= 0 ? '+' : ''}${priceOffset}` : '';
              return `Best Ask${offsetVal} (${bestAsk?.toFixed(2) ?? 'N/A'})`;
          } else if (limitPrice) {
              return parseFloat(limitPrice).toFixed(2); // Absolute
          }
      } else if (orderType === 'market') {
          return 'Market Price';
      }
      return 'N/A';
  };

  return (
    <PreviewContainer>
      <PreviewTitle>Order Preview</PreviewTitle>
      
      {/* Entry Details */}
      <DetailRow>
        <DetailLabel>Symbol</DetailLabel>
        <DetailValue>{symbol}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Side</DetailLabel>
        <DetailValue highlight={side === 'buy'}>{side.toUpperCase()}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Order Type</DetailLabel>
        <DetailValue>{orderType.replace('_', ' ').toUpperCase()}</DetailValue>
      </DetailRow>

      <SectionDivider />
      
      {/* Amount Details */}
      <DetailRow>
        <DetailLabel>Amount ({amountType.toUpperCase()})</DetailLabel>
        <DetailValue>{amount}</DetailValue>
      </DetailRow>
      
      <DetailRow>
        <DetailLabel>Leverage</DetailLabel>
        <DetailValue warning={parseFloat(leverage) > 10}>{leverage}x</DetailValue>
      </DetailRow>

      <SectionDivider />
      
      {/* Entry Price Details */}
      <DetailRow>
        <DetailLabel>Est. Entry Price</DetailLabel>
        <DetailValue>{formatPriceDisplay()}</DetailValue>
      </DetailRow>
      
      {stopPrice && (
        <DetailRow>
          <DetailLabel>Stop Trigger Price</DetailLabel>
          <DetailValue>{parseFloat(stopPrice).toFixed(2)}</DetailValue>
        </DetailRow>
      )}
      
      {takeProfitPrice && (
        <DetailRow>
          <DetailLabel>Take Profit</DetailLabel>
          <DetailValue>{parseFloat(takeProfitPrice).toFixed(2)}</DetailValue>
        </DetailRow>
      )}
      
      {stopLossPrice && (
        <DetailRow>
          <DetailLabel>Stop Loss</DetailLabel>
          <DetailValue>{parseFloat(stopLossPrice).toFixed(2)}</DetailValue>
        </DetailRow>
      )}

      <SectionDivider />
      
      {/* Position Details */}
      <DetailRow>
        <DetailLabel>Est. Position Value</DetailLabel>
        <DetailValue>{positionValue ? `$${positionValue.toFixed(2)}` : 'N/A'}</DetailValue>
      </DetailRow>
      
      <DetailRow>
        <DetailLabel>Required Margin</DetailLabel>
        <DetailValue>{requiredMargin ? `$${requiredMargin.toFixed(2)}` : 'N/A'}</DetailValue>
      </DetailRow>
      
      <DetailRow>
        <DetailLabel>Margin Impact</DetailLabel>
        <DetailValue warning={marginImpact !== null && marginImpact > 20}>
          {marginImpact ? `${marginImpact.toFixed(2)}%` : 'N/A'}
        </DetailValue>
      </DetailRow>
      
      <DetailRow>
        <DetailLabel>Est. Fee</DetailLabel>
        <DetailValue>{estimatedFee ? `$${estimatedFee.toFixed(4)}` : 'N/A'}</DetailValue>
      </DetailRow>
      
      <DetailRow>
        <DetailLabel>Est. Liquidation Price</DetailLabel>
        <DetailValue danger={isDangerousLeverage}>
          {liquidationPrice ? `$${liquidationPrice.toFixed(2)}` : 'N/A'}
        </DetailValue>
      </DetailRow>
      
      {liquidationPercentage !== null && (
        <DetailRow>
          <DetailLabel>Distance to Liquidation</DetailLabel>
          <DetailValue danger={isDangerousLeverage}>
            {liquidationPercentage.toFixed(2)}%
          </DetailValue>
        </DetailRow>
      )}

      <SectionDivider />
      
      {/* Margin Mode and Flags */}
      <DetailRow>
        <DetailLabel>Margin Mode</DetailLabel>
        <DetailValue>{marginMode.toUpperCase()}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Flags</DetailLabel>
        <DetailValue>
          {postOnly ? 'Post-Only ' : ''}
          {reduceOnly ? 'Reduce-Only' : ''}
          {!postOnly && !reduceOnly ? 'None' : ''}
        </DetailValue>
      </DetailRow>

      {isDangerousLeverage && (
        <WarningBox>
          Warning: Liquidation price is very close ({liquidationPercentage?.toFixed(2)}% away). 
          Consider reducing leverage or position size.
        </WarningBox>
      )}

      {error && (
        <DetailRow>
          <DetailLabel style={{ color: '#f87171' }}>Error:</DetailLabel>
          <DetailValue warning>{error}</DetailValue>
        </DetailRow>
      )}
    </PreviewContainer>
  );
};

export default OrderPreview; 